import React, { CSSProperties, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@amzn/awsui-components-react";
import { PropertyFilterInput } from "./PropertyFilterInput";
import { RootState } from "../app/rootReducer";
import {
  PropertyFilter,
  PropertyFilterGroup,
  setQueryMode,
  setQueryString,
} from "../searchSideBar/searchSideBarConnector";
import { getFilterWithAggregatedData } from "../searchSideBar/filterWithAggregatedDataConnector";
import { getProgramNames } from "../searchSideBar/upstreamAttributesConnector";
import { getProductLines } from "../searchSideBar/productLinesConnector";
import { setAsinPageNumber } from "../porAsinPageControl/porAsinPageControl";
import { setValidationPageNumber } from "../validation/asinValidationTableConnector";
import { setBaselinePageNumber } from "../dateBaseline/baselineTableConnector";
import { trackButtonClick } from "../telemetry/telemetry";

/**
 * Horizontal-row layout: the property filter stretches to fill the
 * available width and the Submit button sits pinned to the right in
 * the same row, matching the design mockup.
 *
 * Kept as a module-level constant so React does not build a fresh style
 * object on every render.
 */
const ROW_STYLE: CSSProperties = {
  display: "flex",
  gap: "8px",
  alignItems: "flex-start",
};

const GROW_STYLE: CSSProperties = { flex: 1, minWidth: 0 };

/**
 * True when the given `PropertyFilterGroup` contains at least one
 * non-empty filter (either a top-level `PropertyFilter` with a
 * non-blank value, or a nested `PropertyFilterGroup` with any
 * `filters` of its own). Used to disable the Submit button when the
 * user has not typed any filter yet - a `setQueryString()` dispatch
 * against an empty state produces the `(all)` query, which is not a
 * meaningful search on the App Shell.
 */
function hasAnyFilter(g: PropertyFilterGroup | undefined): boolean {
  if (!g || !g.filters || g.filters.length === 0) return false;
  return g.filters.some((f: PropertyFilter | PropertyFilterGroup) => {
    if (f.type === "PropertyFilter") {
      if (typeof f.value === "string") return f.value.trim().length > 0;
      return f.value !== undefined && f.value !== null;
    }
    return !!f.filters && f.filters.length > 0;
  });
}

/**
 * Shared App Shell Filter Bar.
 *
 * Mounted once by `AppShell.tsx` above the routed content so it renders
 * on every primary Left Nav view (`/v2/search`, `/v2/baseline`,
 * `/v2/validation`, `/v2/launch`). All four views read and write the
 * Applied Filter through the existing `generateQuery` Redux slice - the
 * Filter Bar introduces **no new slice** and no per-view local copy of
 * the filter state (R3.4, R8.1).
 *
 * ## Phase 1 render
 *
 * A single horizontal row:
 *
 * 1. `<PropertyFilterInput/>` on the left, stretched to fill the row
 *    (R5.6, R8.2). The Cloudscape `PropertyFilter` primitive already
 *    exposes its own "Clear filters" chip control when any token is
 *    present, so no separate Reset button is needed.
 * 2. A `Submit` button pinned to the right of the row that promotes
 *    the current Pending Filter to the Applied Filter via
 *    `setQueryString()` and resets the three per-view page counters
 *    to page 1 (LEAP-7748), matching Legacy's Submit behavior. Submit
 *    is disabled when no attribute filter has been added yet, so a
 *    click cannot produce an empty `(all)` search.
 *
 * ## App Shell mount-time bootstraps
 *
 * On mount the Filter Bar dispatches the three data-loading actions
 * that Legacy `SearchSideBar` runs on its own mount. Without these,
 * the property filter's Program and Product Line dropdowns would be
 * empty in the App Shell:
 *   - `getFilterWithAggregatedData` -> attribute buckets
 *   - `getProgramNames`             -> Program dropdown options
 *   - `getProductLines`             -> Product Line dropdown options
 *
 * The Filter Bar also forces `queryMode = "PropertyFilter"` because
 * the App Shell has no `UIFilter` multi-select controls. Without this
 * override, `setQueryString()` on Submit would fall through to the
 * `UIFilter` branch of the slice reducer and read from the (empty)
 * `queryParameters` object instead of the user's `queryPropertyFilters`
 * edits, silently producing a `(all)` query.
 *
 * ## Phase 2 additions (deferred - tasks 51, 53, 8)
 *
 * - `<SystemPresetSelect/>` - task 51 (hardcoded system presets)
 * - `<FilterTokens/>` - task 8
 * - Copy Link button - task 53
 */
export const FilterBar: React.FC = () => {
  const dispatch = useDispatch();
  const { queryMode, pendingQueryString, queryPropertyFilters } = useSelector(
    (state: RootState) => state.generateQuery
  );
  const { isEditing } = useSelector(
    (state: RootState) => state.asinBaselineTableControl
  );
  const { filterWithAggregatedData } = useSelector(
    (state: RootState) => state.filterWithAggregatedData
  );

  // Recompute the "any filter?" flag whenever the property-filter state
  // in Redux changes. Cheap traversal - at most one nested group.
  const canSubmit = useMemo(
    () => hasAnyFilter(queryPropertyFilters),
    [queryPropertyFilters]
  );

  // Defensive: `PorAsinListTable` (mounted as a sibling under `<Routes/>`)
  // dispatches `loadFromUrl()` on its own mount, which resets `queryMode`
  // to whatever the URL hash encodes and defaults to `"UIFilter"` when
  // the hash is empty. Any drift back to `UIFilter` here silently
  // breaks Submit (the reducer takes the UIFilter branch and ignores
  // the property-filter edits), so we snap it back to `PropertyFilter`.
  useEffect(() => {
    if (queryMode !== "PropertyFilter") {
      dispatch(setQueryMode("PropertyFilter"));
    }
  }, [queryMode]);

  useEffect(() => {
    dispatch(getFilterWithAggregatedData());
  }, []);

  useEffect(() => {
    if (filterWithAggregatedData?.status === "finished") {
      dispatch(getProgramNames());
      dispatch(getProductLines());
    }
  }, [filterWithAggregatedData]);

  const onSubmit = () => {
    trackButtonClick("filterBar", "Submit", {
      queryMode,
      query: pendingQueryString,
    });
    // Belt-and-suspenders: guarantee the reducer that runs next sees
    // the App Shell's `PropertyFilter` mode regardless of any pending
    // drift from `loadFromUrl` on a route change.
    dispatch(setQueryMode("PropertyFilter"));
    dispatch(setQueryString());
    // Reset page number on fresh search. See LEAP-7748.
    dispatch(setAsinPageNumber({ pageNumber: 1 }));
    dispatch(setValidationPageNumber({ pageNumber: 1 }));
    dispatch(setBaselinePageNumber({ pageNumber: 1 }));
  };

  return (
    <div data-testid="filter-bar" style={ROW_STYLE}>
      <div style={GROW_STYLE}>
        <PropertyFilterInput />
      </div>
      <Button
        data-testid="filter-bar-submit-button"
        variant="primary"
        disabled={isEditing || !canSubmit}
        onClick={onSubmit}
      >
        Submit
      </Button>
    </div>
  );
};

export default FilterBar;
